import { useState } from "react"

import { checkWorkspaceSubdomain } from "@productlane/lib/src/domain"
import { Button, Dialog, Input } from "@productlane/ui"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"

/** Changes the workspace's productlane.com subdomain; the old one keeps forwarding. */
export function RenameSubdomainDialog({
	isOpen,
	onOpenChange,
	current,
	rootDomain,
}: {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	current: string
	rootDomain: string
}) {
	const [value, setValue] = useState(current)
	const [serverError, setServerError] = useState<string | null>(null)
	const rename = api.workspace.renameSubdomain.useMutation()
	const { subdomain, error } = checkWorkspaceSubdomain(value)
	const unchanged = subdomain === current
	const shownError = value && !unchanged ? (error ?? serverError) : null

	const save = async () => {
		if (error || unchanged || rename.isLoading) return
		setServerError(null)
		try {
			const { domain } = await rename.mutateAsync({ subdomain })
			analytics.track("rename-workspace-subdomain")
			toast.success({
				title: `Your portal now lives at ${domain}.${rootDomain}`,
			})
			onOpenChange(false)
		} catch (mutationError) {
			setServerError(
				mutationError instanceof Error
					? mutationError.message
					: "Couldn't change the address. Try again.",
			)
		}
	}

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (open) {
					setValue(current)
					setServerError(null)
				}
				onOpenChange(open)
			}}
			title="Change portal address"
			description={`Links to ${current}.${rootDomain} keep working and forward to the new address.`}
			footer={
				<Button
					isLoading={rename.isLoading}
					disabled={!!error || unchanged}
					onClick={() => void save()}
				>
					Save
				</Button>
			}
		>
			<div className="mt-3 flex flex-col gap-1.5">
				<Input
					name="workspace-subdomain"
					inputClassName="h-9 rounded-lg"
					value={value}
					errorMessage={shownError ?? undefined}
					autoFocus
					onChange={(event) => {
						setValue(event.target.value)
						setServerError(null)
					}}
					onKeyDown={(event) => {
						if (event.key === "Enter") void save()
					}}
				/>
				<p className="truncate font-mono text-xs text-secondary">
					{subdomain || current}.{rootDomain}
				</p>
			</div>
		</Dialog>
	)
}
